


















































import { getFormInputTextMixin } from '@/mixins/formInputTextMixin';
import { TextField } from '@/types/forms/fields/textField';
import { TextInputWithUnitContent } from '@/api/interfaces/content/form/base/textInputWithUnitContent';
import FieldWithInfoIcon from '@/components/molecules/fieldWithInfoIcon/FieldWithInfoIcon.vue';
import Icon from '@/components/atoms/icon/Icon.vue';
import { sanitizeTextInputValue } from '@/utilities/stringParsingUtils';

export default getFormInputTextMixin<
    TextField,
    TextInputWithUnitContent
>().extend({
    name: 'FormInputText',
    components: {
        FieldWithInfoIcon,
        Icon,
    },
    props: {
        readonly: {
            type: Boolean,
            default: false,
        },
    },
    methods: {
        processInput(): void {
            let { value } = this.model;
            value = sanitizeTextInputValue(value);
            value = this.formatValueWithSeparator(value);
            this.model.value = value;
        },
        formatValueWithSeparator(value: string): string {
            if (
                !this.validations ||
                !Object.prototype.hasOwnProperty.call(
                    this.validations,
                    'float',
                ) ||
                !value
            ) {
                return value;
            }

            const inputEl = this.$refs.inputEl as HTMLInputElement | undefined;
            const origCaret = inputEl ? inputEl.selectionStart : null;
            const origValue = value;

            // Step 1: Sanitize (as before)
            // First allow digits, dot, comma, +, -
            let work = value.replace(/[^0-9.,+-]/g, '');
            // Step 2: Remove all existing dots (old separators)
            work = work.replace(/\.+/g, '');
            // Step 3: Only digits + optionally one comma (decimal part)
            // Reduce multiple commas to the first one.
            const firstComma = work.indexOf(',');
            if (firstComma !== -1) {
                const intPart = work
                    .slice(0, firstComma)
                    .replace(/[^0-9]/g, '');
                const decPart = work
                    .slice(firstComma + 1)
                    .replace(/[^0-9]/g, '');
                work = intPart + ',' + decPart;
            } else {
                work = work.replace(/[^0-9]/g, '');
            }

            // Adjust caret after sanitizing steps:
            let caret = origCaret;
            if (caret !== null) {
                // Compute difference of length before caret (original vs sanitized)
                const prefixOriginal = origValue.slice(0, caret);
                // Apply same steps on prefix (to know how many characters disappeared)
                let prefixWork = prefixOriginal.replace(/[^0-9.,+-]/g, '');
                prefixWork = prefixWork.replace(/\.+/g, '');
                if (firstComma !== -1) {
                    const pFirstComma = prefixWork.indexOf(',');
                    if (pFirstComma !== -1) {
                        const pInt = prefixWork
                            .slice(0, pFirstComma)
                            .replace(/[^0-9]/g, '');
                        const pDec = prefixWork
                            .slice(pFirstComma + 1)
                            .replace(/[^0-9]/g, '');
                        prefixWork = pInt + ',' + pDec;
                    } else {
                        prefixWork = prefixWork.replace(/[^0-9]/g, '');
                    }
                } else {
                    prefixWork = prefixWork.replace(/[^0-9]/g, '');
                }
                caret = prefixWork.length; // new position in sanitized string (before thousand separators)
            }

            // Step 4: Add thousand separators only in the integer part
            const commaIndex = work.indexOf(',');
            const intPart =
                commaIndex === -1 ? work : work.slice(0, commaIndex);
            const decPart = commaIndex === -1 ? '' : work.slice(commaIndex); // including comma

            // Build mapping for integer part
            const digits = intPart;
            const L = digits.length;
            let formattedInt = '';
            // Map: originalIndex -> formattedIndex (position AFTER this digit)
            const map: number[] = new Array(L);

            for (let i = 0; i < L; i++) {
                // Insert separator when remaining digits count is a multiple of 3 (and not at start)
                const remaining = L - i;
                if (i !== 0 && remaining % 3 === 0) {
                    formattedInt += '.';
                }
                formattedInt += digits[i];
                map[i] = formattedInt.length; // caret after this digit
            }

            const formatted = formattedInt + decPart;
            let finalCaret = caret;

            if (caret !== null) {
                // Is caret inside integer part?
                if (commaIndex === -1 || caret <= commaIndex) {
                    // caret refers to position within intPart (after sanitize, before dots)
                    const posInInt = Math.min(caret, intPart.length);
                    if (posInInt === 0) {
                        finalCaret = 0;
                    } else {
                        finalCaret = map[posInInt - 1];
                    }
                } else {
                    // Caret is in decimal part: shift by number of inserted dots
                    const totalInserted = formattedInt.length - intPart.length;
                    finalCaret = caret + totalInserted;
                }
            }

            // Set value
            value = formatted;

            if (inputEl && finalCaret !== null) {
                const finalCaretVal = finalCaret;
                this.$nextTick(() => {
                    // Limit if necessary
                    const safeCaret = Math.min(finalCaretVal, value.length);
                    inputEl.setSelectionRange(safeCaret, safeCaret);
                });
            }

            return value;
        },
    },
});
